/**
 * Centralized API service for communicating with the backend.
 * Automatically attaches the Authorization header from localStorage.
 */

import toast from 'react-hot-toast';

const API_BASE = '/api';

// In-flight GET promise cache for request deduplication
const inFlightGetRequests = new Map();
let activeRateLimitToastId = null;

async function executeFetchWithRetry(path, options = {}, retryCount = 0) {
  const token = localStorage.getItem('token');
  const method = (options.method || 'GET').toUpperCase();
  const isGet = method === 'GET';

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // Redirect to login on unauthorized
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  // Handle 429 Too Many Requests (automatic retry up to 3 times for GET requests)
  if (response.status === 429 && isGet && retryCount < 3) {
    const retryAfterHeader = response.headers.get('Retry-After');
    let retryAfterSec = parseInt(retryAfterHeader, 10);
    if (isNaN(retryAfterSec) || retryAfterSec <= 0) {
      // Exponential backoff: 2s, 4s, 8s
      retryAfterSec = Math.pow(2, retryCount + 1);
    }
    // Cap wait to 15s max per retry attempt to avoid freezing
    retryAfterSec = Math.min(retryAfterSec, 15);

    const msg = `Muitas requisições. Tentando novamente em ${retryAfterSec}s (tentativa ${retryCount + 1}/3)...`;
    if (activeRateLimitToastId) {
      toast.loading(msg, { id: activeRateLimitToastId });
    } else {
      activeRateLimitToastId = toast.loading(msg);
    }

    await new Promise(resolve => setTimeout(resolve, retryAfterSec * 1000));

    return executeFetchWithRetry(path, options, retryCount + 1);
  }

  if (activeRateLimitToastId) {
    toast.dismiss(activeRateLimitToastId);
    activeRateLimitToastId = null;
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const err = new Error(
      response.status === 429
        ? 'Muitas requisições ao servidor. Por favor, aguarde alguns instantes e tente novamente.'
        : (data?.message || data?.error || `Request failed: ${response.status}`)
    );
    err.status = response.status;
    err.data = data;
    throw err;
  }

  return data;
}

/**
 * Core fetch wrapper with auth header injection, in-flight deduplication and 429 retry.
 */
export async function apiFetch(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isGet = method === 'GET';

  // Deduplicate identical in-flight GET requests
  if (isGet) {
    const dedupeKey = path;
    if (inFlightGetRequests.has(dedupeKey)) {
      return inFlightGetRequests.get(dedupeKey);
    }
    const promise = executeFetchWithRetry(path, options, 0)
      .finally(() => {
        inFlightGetRequests.delete(dedupeKey);
      });
    inFlightGetRequests.set(dedupeKey, promise);
    return promise;
  }

  return executeFetchWithRetry(path, options, 0);
}

/**
 * Downloads a file (e.g. XLSX export) from an authenticated endpoint and triggers
 * a browser download. Can't use a plain `<a href>` for these since they require
 * the Bearer token header.
 */
export async function apiDownload(path, fallbackFilename = 'download.xlsx') {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    try {
      const data = await response.json();
      message = data?.message || data?.error || message;
    } catch {
      // Response wasn't JSON (e.g. a raw error page) — keep the generic message.
    }
    throw new Error(message);
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackFilename;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// --- Auth ---
export const authApi = {
  login: (email, password) =>
    apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  me: () => apiFetch('/auth/me'),
  logout: () => apiFetch('/auth/logout', { method: 'POST' }),
};

// --- Meta ---
export const metaApi = {
  getAuthUrl: () => apiFetch('/meta/auth/url'),
  handleCallback: (code) =>
    apiFetch('/meta/auth/callback', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  getAccounts: () => apiFetch('/meta/accounts'),
  syncAccounts: () => apiFetch('/meta/accounts/sync', { method: 'POST' }),
  getFinancials: (accountId) => apiFetch(`/meta/accounts/${accountId}/financial`),
  getCampaigns: (accountId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/meta/accounts/${accountId}/campaigns${query ? `?${query}` : ''}`);
  },
  getInsights: (accountId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/meta/accounts/${accountId}/insights${query ? `?${query}` : ''}`);
  },
  getPages: () => apiFetch('/meta/pages'),
  getPageInsights: (pageId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/meta/pages/${pageId}/insights${query ? `?${query}` : ''}`);
  },
  getLeadForms: (pageId) => apiFetch(`/meta/pages/${pageId}/forms`),
  getFormLeads: (formId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/meta/forms/${formId}/leads${query ? `?${query}` : ''}`);
  },
  getAllLeads: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/meta/leads${query ? `?${query}` : ''}`);
  },
  exportAllLeads: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiDownload(`/meta/leads/export${query ? `?${query}` : ''}`, 'todos_os_leads.xlsx');
  },
  exportFormLeads: (formId, filename) => apiDownload(`/meta/forms/${formId}/leads/export`, filename || 'leads.xlsx'),
  getConnections: () => apiFetch('/meta/connections'),
  getAllAccountsFromDb: () => apiFetch('/meta/accounts/all'),
  getLeadsStats: () => apiFetch('/webhooks/stats'),
  syncPages: () => apiFetch('/meta/pages/sync', { method: 'POST' }),
  getPageSubscriptions: () => apiFetch('/meta/pages/subscriptions'),
  verifyPageSubscriptions: () => apiFetch('/meta/pages/subscriptions/verify', { method: 'POST' }),
  subscribePage: (pageId) => apiFetch(`/meta/pages/${pageId}/subscribe`, { method: 'POST' }),
  unsubscribePage: (pageId) => apiFetch(`/meta/pages/${pageId}/subscribe`, { method: 'DELETE' }),
  updatePageWebhook: (pageId, data) => apiFetch(`/meta/pages/${pageId}/webhook`, { method: 'PUT', body: JSON.stringify(data) }),
  testPageWebhook: (pageId) => apiFetch(`/meta/pages/${pageId}/webhook/test`, { method: 'POST' }),
  getSyncStatus: () => apiFetch('/meta/sync-status'),
  getAdSets: (accountId, campaignId) => {
    const q = campaignId ? `?campaignId=${campaignId}` : '';
    return apiFetch(`/meta/accounts/${accountId}/adsets${q}`);
  },
  getAds: (accountId, adsetId) => {
    const q = adsetId ? `?adsetId=${adsetId}` : '';
    return apiFetch(`/meta/accounts/${accountId}/ads${q}`);
  },
};

// --- Google Ads (Admin helpers) ---
export const googleAdsApi = {
  getAllAccountsFromDb: () => apiFetch('/google-ads/accounts/all'),
};

// --- Dashboard ---
export const dashboardApi = {
  getSummary: (period = 'month') => apiFetch(`/dashboard/summary?period=${encodeURIComponent(period)}`),
};

// --- Users (Admin) ---
export const usersApi = {
  getUsers: () => apiFetch('/users'),
  createUser: (data) => apiFetch('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => apiFetch(`/users/${id}`, { method: 'DELETE' }),
};

// --- Combined Reports ---
export const combinedApi = {
  getDemographics: (metaAccountIds, startDate, endDate, objective = 'ALL') => {
    const params = new URLSearchParams({ metaAccountIds, startDate, endDate, objective }).toString();
    return apiFetch(`/combined/demographics?${params}`);
  },
};

// --- AI Assistant ---
export const aiApi = {
  analyzeReport: (campaignData) =>
    apiFetch('/ai/analyze-report', {
      method: 'POST',
      body: JSON.stringify({ campaignData }),
    }),
  analyzeClinicUnits: (unitsData) =>
    apiFetch('/ai/analyze-clinic-units', {
      method: 'POST',
      body: JSON.stringify({ unitsData }),
    }),
};

// --- Reports ---
export const reportsApi = {
  shareReport: (data) => apiFetch('/reports/share', { method: 'POST', body: JSON.stringify(data) }),
  getSharedReport: (token) => fetch(`/api/reports/shared/${token}`).then(res => res.json()),
  listShares: () => apiFetch('/reports/shares'),
  deleteShare: (id) => apiFetch(`/reports/shares/${id}`, { method: 'DELETE' }),
  getLeadGoalUnits: () => apiFetch('/reports/lead-goals/units'),
  getLeadGoals: ({ objective, unitIds = [], year, month }) => {
    const params = new URLSearchParams({
      objective,
      unit_ids: unitIds.join(','),
      ...(year && { year }),
      ...(month && { month }),
    }).toString();
    return apiFetch(`/reports/lead-goals?${params}`);
  },
  getLeadGoal: (accountId, year, month) => {
    const params = new URLSearchParams({
      account_id: accountId,
      ...(year && { year }),
      ...(month && { month }),
    }).toString();
    return apiFetch(`/reports/lead-goal?${params}`);
  },
};

// --- Clinicorp ---
export const clinicorpApi = {
  getAccounts: () => apiFetch('/clinicorp/accounts'),
  getAllAccounts: () => apiFetch('/clinicorp/accounts/all'),
  createAccount: (data) => apiFetch('/clinicorp/accounts', { method: 'POST', body: JSON.stringify(data) }),
  updateAccount: (id, data) => apiFetch(`/clinicorp/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAccount: (id) => apiFetch(`/clinicorp/accounts/${id}`, { method: 'DELETE' }),
  getAnalytics: (id, from, to) => apiFetch(`/clinicorp/accounts/${id}/analytics?from=${from}&to=${to}`),
};

// --- Franchise Reports ---
export const franchiseApi = {
  getReports: () => apiFetch('/franchise'),
  getReport: (id) => apiFetch(`/franchise/${id}`),
  createReport: (data) => apiFetch('/franchise', { method: 'POST', body: JSON.stringify(data) }),
  updateReport: (id, data) => apiFetch(`/franchise/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteReport: (id) => apiFetch(`/franchise/${id}`, { method: 'DELETE' }),
};

// --- Conversions (WhatsApp CTWA + Meta CAPI + Google OCI) ---
export const conversionsApi = {
  // Links
  getLinks: () => apiFetch('/conversions/links'),
  createLink: (data) => apiFetch('/conversions/links', { method: 'POST', body: JSON.stringify(data) }),
  updateLink: (id, data) => apiFetch(`/conversions/links/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  publishLink: (id) => apiFetch(`/conversions/links/${id}/publish`, { method: 'POST' }),
  deleteLink: (id) => apiFetch(`/conversions/links/${id}`, { method: 'DELETE' }),
  // Leads
  getLeads: (params = {}) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))).toString();
    return apiFetch(`/conversions/leads${q ? `?${q}` : ''}`);
  },
  updateLeadStatus: (id, status) => apiFetch(`/conversions/leads/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  // WhatsApp
  getWabas: () => apiFetch('/conversions/whatsapp'),
  createWaba: (data) => apiFetch('/conversions/whatsapp', { method: 'POST', body: JSON.stringify(data) }),
  updateWaba: (id, data) => apiFetch(`/conversions/whatsapp/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getPhones: (wabaId) => apiFetch(`/conversions/whatsapp/${wabaId}/phones`),
  addPhone: (wabaId, data) => apiFetch(`/conversions/whatsapp/${wabaId}/phones`, { method: 'POST', body: JSON.stringify(data) }),
  // Feedback (CRM bottom-funnel)
  sendFeedback: (data) => apiFetch('/conversions/feedback', { method: 'POST', body: JSON.stringify(data) }),
  // Stats
  getStats: () => apiFetch('/conversions/stats'),
  // Chatwoot
  getChatwootWebhookUrl: () => apiFetch('/conversions/chatwoot/webhook-url'),
  getChatwootAccounts: () => apiFetch('/conversions/chatwoot'),
  createChatwootAccount: (data) => apiFetch('/conversions/chatwoot', { method: 'POST', body: JSON.stringify(data) }),
  updateChatwootAccount: (id, data) => apiFetch(`/conversions/chatwoot/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteChatwootAccount: (id) => apiFetch(`/conversions/chatwoot/${id}`, { method: 'DELETE' }),
  getChatwootInboxes: (id) => apiFetch(`/conversions/chatwoot/${id}/inboxes`),
  setupChatwootAttributes: (id) => apiFetch(`/conversions/chatwoot/${id}/setup-attributes`, { method: 'POST' }),
};

// --- Clinic Data ---
export const clinicFunnelApi = {
  getMappings: () => apiFetch('/clinic-data/funnel/mappings'),
  createMapping: (data) => apiFetch('/clinic-data/funnel/mappings', { method: 'POST', body: JSON.stringify(data) }),
  updateMapping: (id, data) => apiFetch(`/clinic-data/funnel/mappings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteMapping: (id) => apiFetch(`/clinic-data/funnel/mappings/${id}`, { method: 'DELETE' }),
  buildDraft: (data) => apiFetch('/clinic-data/funnel/draft', { method: 'POST', body: JSON.stringify(data) }),
  getReports: () => apiFetch('/clinic-data/funnel/reports'),
  getReport: (id) => apiFetch(`/clinic-data/funnel/reports/${id}`),
  createReport: (data) => apiFetch('/clinic-data/funnel/reports', { method: 'POST', body: JSON.stringify(data) }),
  updateReport: (id, data) => apiFetch(`/clinic-data/funnel/reports/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteReport: (id) => apiFetch(`/clinic-data/funnel/reports/${id}`, { method: 'DELETE' }),
  refreshDraft: (id) => apiFetch(`/clinic-data/funnel/reports/${id}/refresh-draft`),
};

export const clinicDataApi = {
  // Routines
  getRoutines: () => apiFetch('/clinic-data/routines'),
  getRoutine: (id) => apiFetch(`/clinic-data/routines/${id}`),
  createRoutine: (data) => apiFetch('/clinic-data/routines', { method: 'POST', body: JSON.stringify(data) }),
  updateRoutine: (id, data) => apiFetch(`/clinic-data/routines/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRoutine: (id) => apiFetch(`/clinic-data/routines/${id}`, { method: 'DELETE' }),
  triggerRoutine: (id) => apiFetch(`/clinic-data/routines/${id}/trigger`, { method: 'POST' }),
  triggerRoutineForDate: (id, date) => apiFetch(`/clinic-data/routines/${id}/trigger-date`, { method: 'POST', body: JSON.stringify({ date }) }),
  getCollections: (routineId, page = 1, limit = 30, startDate, endDate) => {
    const params = new URLSearchParams({ page, limit, ...(startDate && { startDate }), ...(endDate && { endDate }) }).toString();
    return apiFetch(`/clinic-data/routines/${routineId}/collections?${params}`);
  },
  getRoutineSummary: (routineId, startDate, endDate) => {
    const params = new URLSearchParams({ ...(startDate && { startDate }), ...(endDate && { endDate }) }).toString();
    return apiFetch(`/clinic-data/routines/${routineId}/summary${params ? `?${params}` : ''}`);
  },
  getRoutineSummaryBatch: (routineIds, startDate, endDate) =>
    apiFetch('/clinic-data/routines/summary-batch', {
      method: 'POST',
      body: JSON.stringify({ routineIds, startDate, endDate }),
    }),
  // Saved multi-unit report groups (e.g. "Unidades Apure Saúde")
  getGroups: () => apiFetch('/clinic-data/groups'),
  createGroup: (data) => apiFetch('/clinic-data/groups', { method: 'POST', body: JSON.stringify(data) }),
  updateGroup: (id, data) => apiFetch(`/clinic-data/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGroup: (id) => apiFetch(`/clinic-data/groups/${id}`, { method: 'DELETE' }),
  // Collections
  getCollection: (id) => apiFetch(`/clinic-data/collections/${id}`),
  resendWebhook: (id) => apiFetch(`/clinic-data/collections/${id}/resend`, { method: 'POST' }),
  manualFill: (id, data) => apiFetch(`/clinic-data/collections/${id}/entry`, { method: 'PUT', body: JSON.stringify(data) }),
  // Report data
  getAccountData: (adAccountIds, startDate, endDate) => {
    const ids = Array.isArray(adAccountIds) ? adAccountIds.join(',') : adAccountIds;
    const params = new URLSearchParams({ adAccountIds: ids, ...(startDate && { startDate }), ...(endDate && { endDate }) }).toString();
    return apiFetch(`/clinic-data/account-data?${params}`);
  },
  // Public form (no auth needed, use raw fetch)
  getFormData: (token) => fetch(`/api/clinic-data/form/${token}`).then(r => r.json()),
  submitForm: (token, data) => fetch(`/api/clinic-data/form/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(r => r.json()),
};

// --- Lead Source Mappings (Admin) — domain -> Facebook Page de-para used to route
// imported landing-page leads (e.g. Apure/n8n) into the right page's access control.
export const leadSourceMappingsApi = {
  list: () => apiFetch('/lead-source-mappings'),
  create: (data) => apiFetch('/lead-source-mappings', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/lead-source-mappings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/lead-source-mappings/${id}`, { method: 'DELETE' }),
};

// --- Franchise V2 (Meta + Clinicorp) ---
export const franchiseV2Api = {
  getReports: () => apiFetch('/franchise-v2/reports'),
  createReport: (data) => apiFetch('/franchise-v2/reports', { method: 'POST', body: JSON.stringify(data) }),
  updateReport: (id, data) => apiFetch(`/franchise-v2/reports/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteReport: (id) => apiFetch(`/franchise-v2/reports/${id}`, { method: 'DELETE' }),
  getMappings: (clinicorpAccountId) => apiFetch(`/franchise-v2/mappings/${clinicorpAccountId}`),
  saveMappings: (clinicorpAccountId, mappings) =>
    apiFetch(`/franchise-v2/mappings/${clinicorpAccountId}`, { method: 'PUT', body: JSON.stringify({ mappings }) }),
  generate: (data) => apiFetch('/franchise-v2/generate', { method: 'POST', body: JSON.stringify(data) }),
};

// --- CRM Forms (formulários públicos /f/:slug) ---
const jsonBody = (data) => JSON.stringify(data);
export const crmFormsApi = {
  getAccounts: () => apiFetch('/crm-forms/accounts'),
  list: () => apiFetch('/crm-forms'),
  get: (id) => apiFetch(`/crm-forms/${id}`),
  create: (data) => apiFetch('/crm-forms', { method: 'POST', body: jsonBody(data) }),
  update: (id, data) => apiFetch(`/crm-forms/${id}`, { method: 'PUT', body: jsonBody(data) }),
  remove: (id) => apiFetch(`/crm-forms/${id}`, { method: 'DELETE' }),
  getStats: (id) => apiFetch(`/crm-forms/${id}/stats`),
  getSubmissions: (id, { page = 1, limit = 20 } = {}) =>
    apiFetch(`/crm-forms/${id}/submissions?page=${page}&limit=${limit}`),
  exportCsv: (id, slug) => apiDownload(`/crm-forms/${id}/export.csv`, `respostas-${slug || id}.csv`),
  createPreviewToken: (id) => apiFetch(`/crm-forms/${id}/preview-token`, { method: 'POST', body: jsonBody({}) }),
  testWebhook: (id) => apiFetch(`/crm-forms/${id}/webhook/test`, { method: 'POST', body: jsonBody({}) }),
  getDeliveries: (id) => apiFetch(`/crm-forms/${id}/deliveries`),
};

export const crmIntegrationApi = {
  listAccounts: () => apiFetch('/crm-integration/accounts')
};
